/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import NotificationsPanel from './NotificationsPanel';
import { apiGet } from '../../services/apiClient';
import { extractErrorMessage, logError } from '../../utils/errorHandler';
import { getAuthToken } from '../../services/authStorage';

let lastUnreadFetchAt = 0;
let lastUnreadFetchPromise = null;

const NotificationBell = ({ language, currentUser }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const fetchUnreadCount = useCallback(async (force = false) => {
    try {
      if (!currentUser || !getAuthToken('erp') || (typeof window !== 'undefined' && window.sessionStorage.getItem('sav_erp_auth_ready') !== '1')) {
        setUnreadCount(0);
        return;
      }

      const now = Date.now();
      if (!force && lastUnreadFetchPromise && now - lastUnreadFetchAt < 60000) {
        const cached = await lastUnreadFetchPromise;
        setUnreadCount(cached?.unread_count || 0);
        return;
      }

      lastUnreadFetchAt = now;
      lastUnreadFetchPromise = apiGet('/notifications/unread-count');
      const response = await lastUnreadFetchPromise;
      setUnreadCount(response?.unread_count || 0);
    } catch (error) {
      logError('NotificationBell.fetchUnreadCount', error);
      const message = extractErrorMessage(error, 'Failed to fetch unread notifications');
      if (process.env.NODE_ENV === 'development') {
        console.warn(message);
      }
      setUnreadCount(0);
    } finally {
      if (Date.now() - lastUnreadFetchAt >= 60000) {
        lastUnreadFetchPromise = null;
      }
    }
  }, [currentUser]);

  useEffect(() => {
    const runIfVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      fetchUnreadCount();
    };

    runIfVisible();
    const interval = setInterval(runIfVisible, 180000);
    const onVisibilityChange = () => runIfVisible();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchUnreadCount]);

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    fetchUnreadCount(true);
  };

  return (
    <>
      <button
        onClick={() => setIsPanelOpen(true)}
        className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
        title={language === 'ar' ? 'الإشعارات' : 'Notifications'}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationsPanel language={language} isOpen={isPanelOpen} onClose={handlePanelClose} />
    </>
  );
};

export default NotificationBell;
